import React from 'react';
import {concatenate} from "services/string";
import {ArrowSmDownIcon, ArrowSmRightIcon, ArrowSmUpIcon} from "@heroicons/react/solid";
import Spinner from "components/Spinner";
import {Icon} from "../../types/Icon";
import Link from "../Link";
import {HashLink} from "react-router-hash-link";
import Tooltip from "../Tooltip";
import {HelpIcon} from "../Icon/ngage";

// Types ----

enum Color {BRAND_GRADIENT, BRAND, BLUE, PURPLE, PINK, GREEN, GRAY}

enum ChangeDirection {NONE, INCREASE, DECREASE}

type StatBoxProps = {
  name: string
  icon: Icon
  color?: Color
  stat: string | number | undefined
  change?: string | number | undefined
  url?: string
  tooltip?: string
  isLoading?: boolean | undefined
}

// Helpers ----

const iconColors: Record<Color, string> = {
  [Color.BRAND_GRADIENT]: 'bg-brand/15 bg-gradient-to-t from-brand/40 to-brand/0',
  [Color.BRAND]: 'bg-brand',
  [Color.BLUE]: 'bg-blue-900',
  [Color.PURPLE]: 'bg-purple-800',
  [Color.PINK]: 'bg-pink-800',
  [Color.GREEN]: 'bg-green-600',
  [Color.GRAY]: 'bg-gray-600',
}

const changeIcons: Record<ChangeDirection, JSX.Element> = {
  [ChangeDirection.INCREASE]: <ArrowSmUpIcon className="self-center flex-shrink-0 h-5 w-5 text-green-500"
                                             aria-hidden="true"/>,
  [ChangeDirection.DECREASE]: <ArrowSmDownIcon className="self-center flex-shrink-0 h-5 w-5 text-red-500"
                                               aria-hidden="true"/>,
  [ChangeDirection.NONE]: <ArrowSmRightIcon className="self-center flex-shrink-0 h-5 w-5 text-gray-500"
                                            aria-hidden="true"/>,
}

const changeColors: Record<ChangeDirection, string> = {
  [ChangeDirection.INCREASE]: 'text-green-600',
  [ChangeDirection.DECREASE]: 'text-red-600',
  [ChangeDirection.NONE]: 'text-gray-600',
}

const changePhrases: Record<ChangeDirection, string> = {
  [ChangeDirection.INCREASE]: 'Increased by',
  [ChangeDirection.DECREASE]: 'Decreased by',
  [ChangeDirection.NONE]: 'Unchanged',
}

function getStatValue(stat: StatBoxProps['stat']) {
  if (stat === undefined) return '?';
  if (typeof stat === 'number') return stat.toLocaleString();
  return stat
}

// Component ----

function StatBox({change, color = Color.BRAND_GRADIENT, icon: Icon, isLoading, name, stat, url, tooltip}: StatBoxProps) {

  let changeFloat, changeDirection;

  if (change !== undefined) {
    changeFloat = parseFloat(change as string)
    changeDirection = changeFloat === 0 ? ChangeDirection.NONE : (changeFloat > 0 ? ChangeDirection.INCREASE : ChangeDirection.DECREASE)
  }

  return (
    <div className="relative bg-white w-full pt-5 px-4 pb-5 sm:pt-6 sm:px-6 shadow sm:rounded-lg">
      <dt>
        <div className={concatenate(iconColors[color], 'absolute rounded-full p-3')}>
          <Icon className={`h-6 w-6 ${color === Color.BRAND_GRADIENT ? 'text-brand' : 'text-white'}`} aria-hidden="true"/>
        </div>

        <p className="ml-16 text-sm font-normal text-gray-500 truncate">{name}</p>

        {!!tooltip &&
          <div className="absolute right-2.5 top-2.5 has-tooltip">
            <Tooltip>{tooltip}</Tooltip>
            <HelpIcon className="h-4 w-4 text-gray-500"/>
          </div>
        }
      </dt>

      <dd className="ml-16">
        {isLoading &&
          <div className="mt-2 h-[1.55rem]">
            <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.X_SMALL}/>
          </div>
        }

        {!isLoading &&
          <div className="flex justify-between items-center w-full">
            <div className="flex items-baseline">
              <p className="text-[1.375rem] font-semibold text-gray-900">{stat === undefined ? '?' : getStatValue(stat)}</p>

              {changeDirection != null &&
                <p
                  className={concatenate(changeColors[changeDirection], 'ml-2 flex items-baseline text-sm font-semibold')}>
                  {changeIcons[changeDirection]}
                  <span className="sr-only">{changePhrases[changeDirection]}</span>
                  {change}
                </p>
              }
            </div>

            {!!url &&
              <div className='text-xs'>
                {!!url && !url.includes('#') &&
                  <Link to={url}>View</Link>
                }

                {!!url && url.includes('#') &&
                  <HashLink
                    to={url}
                    className="font-semibold text-brand border-b border-dashed border-brand"
                    smooth
                  >
                    View
                  </HashLink>
                }
              </div>
            }
          </div>
        }
      </dd>
    </div>
  );
}

StatBox.colors = Color

export default StatBox;